/**
 * Steps 1 to 5 of a room made from a recording: the talk, the three steps the
 * recording works through on its own (upload, making it playable, listening),
 * the names check, and the board being built. Every number on these cards is
 * the server's (bytes sent, pieces encoded, audio heard, read steps landed);
 * nothing here counts on a timer.
 */
import { useEffect, useRef, useState, type DragEvent } from "react";
import { READ_PAGE_STEP, type SceneStep } from "../../../shared/board-copy";
import { uploadLimitLabel, UPLOAD_MAX_BYTES } from "../../../shared/board-caps";
import type { BoardCard, BoardState, BoardStateResponse, HeardNameView, LibraryAsset } from "../../../shared/types";
import { useFlashkutAccount } from "../../assets/flashkut";
import { FkMark } from "../../assets/parts";
import { openAssets } from "../../assets/store";

/** The folder Flashkut's finished talks are on, as the library names it. */
const FLASHKUT_SHELF = "flashkut";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { api } from "../../lib/api";
import { sizeLabel } from "../progress";
import { StepHead } from "./MakeFrame";
import { mmss } from "./time";

// ── Step 1: your talk ────────────────────────────────────────────────────────

interface StepUploadProps {
  /** The talk picked (or just uploaded) in Assets. */
  onTalk: (talk: LibraryAsset) => void;
  error?: string;
  busy?: boolean;
}

/** A recording the upload can take: a video, inside the size ceiling. */
export function talkRefusal(file: File): string {
  if (!file.type.startsWith("video/") && !/\.(mp4|mov|m4v|webm)$/i.test(file.name)) return "That is not a video. Choose an MP4 or a MOV.";
  if (file.size > UPLOAD_MAX_BYTES) return `That recording is over ${uploadLimitLabel()}.`;
  return "";
}

/** Assets, narrowed to full talks; files dropped on the step go up in it and are picked when they are in. */
export function pickTalk(onTalk: (talk: LibraryAsset) => void, incoming?: File[], at?: string, cta = "Make the room from it"): void {
  openAssets({ fits: "talk", title: "Choose your talk", cta, incoming, onPick: onTalk, ...(at ? { at } : {}) });
}

export function StepUpload({ onTalk, error, busy }: StepUploadProps) {
  const flashkut = useFlashkutAccount();
  const [over, setOver] = useState(false);
  const [refused, setRefused] = useState("");
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file || busy) return;
    const why = talkRefusal(file);
    setRefused(why);
    if (!why) pickTalk(onTalk, [file]);
  };
  return (
    <div className="card nbw">
      <StepHead n={1} of={7} title="Your talk" />
      <div
        className={cx("nbdrop", over && "over")}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        <div className="ic">
          <Icon name="upload" />
        </div>
        <div className="b8 nbdrop-h">Drop your talk here</div>
        <div className="muted small">MP4 or MOV, up to {uploadLimitLabel()}</div>
        <button type="button" className="btn p lg mt8" disabled={busy} onClick={() => pickTalk(onTalk)}>
          <Icon name="fold" /> {busy ? "Starting…" : "From Assets, or upload it"}
        </button>
      </div>
      {flashkut ? (
        <div className="note mt16 nbfknote">
          <FkMark />
          <div>
            Made in Flashkut?{" "}
            <button type="button" className="link" onClick={() => pickTalk(onTalk, undefined, FLASHKUT_SHELF)}>
              Open Made in Flashkut
            </button>
          </div>
        </div>
      ) : null}
      {refused || error ? (
        <p className="note warn mt16" role="alert">
          <Icon name="info" />
          <span>{refused || error}</span>
        </p>
      ) : null}
    </div>
  );
}

// ── Steps 1 to 3 while the recording works: upload, playable, listen ──────────

type WorkStep = "upload" | "playable" | "listen";
const WORK_ORDER: readonly WorkStep[] = ["upload", "playable", "listen"];

export interface StepAction {
  label: string;
  onClick: () => void;
}

interface StepWorkProps {
  /** The upload itself happens in Assets: here the talk is made playable, then heard. */
  step: Exclude<WorkStep, "upload">;
  /** Known when this tab picked the talk. */
  sizeBytes: number | null;
  video: BoardStateResponse["video"];
  durationSec: number | null;
  /** The last lines heard, newest last, when the listening has written some. */
  heard: ReadonlyArray<{ ts: string; said: string }>;
  error?: string;
  action?: StepAction;
  second?: StepAction;
}

type RowState = "done" | "run" | "wait";

function rowState(on: WorkStep, row: WorkStep): RowState {
  const a = WORK_ORDER.indexOf(on);
  const b = WORK_ORDER.indexOf(row);
  return a > b ? "done" : a === b ? "run" : "wait";
}

/** The share of the talk heard so far, 0 to 1, from the server's audio pieces. */
export function heardShare(video: BoardStateResponse["video"]): number {
  const total = video.heardTotal ?? 0;
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, (video.heardDone ?? 0) / total));
}

function workLines(p: Pick<StepWorkProps, "step" | "sizeBytes" | "video" | "durationSec">): Record<WorkStep, string> {
  const size = p.sizeBytes ? sizeLabel(p.sizeBytes) : "";
  const minutes = p.durationSec ? Math.max(1, Math.round(p.durationSec / 60)) : 0;
  const play = rowState(p.step, "playable");
  const listen = rowState(p.step, "listen");
  return {
    upload: size ? `${size}, all here` : "All here",
    playable:
      play === "done"
        ? p.video.chunkCount > 0
          ? `${p.video.chunkCount} pieces, all ready`
          : "All ready"
        : play === "run" && p.video.chunkCount > 0
          ? `${p.video.chunksDone} of ${p.video.chunkCount} pieces ready`
          : "Pieces that play anywhere",
    listen:
      listen === "run" && minutes
        ? `${Math.round(heardShare(p.video) * minutes)} of ${minutes} minutes`
        : listen === "run"
          ? "Every word, with its second"
          : "Every word, with its second",
  };
}

const WAVE_BARS = 64;
const wave = Array.from({ length: WAVE_BARS }, (_, i) => 18 + Math.round(Math.abs(Math.sin(i * 1.7) * 26 + Math.sin(i * 0.6) * 14)));

const WORK_HEAD: Record<WorkStep, string> = {
  upload: "Uploading",
  playable: "Making it playable",
  listen: "Listening to the talk",
};

export function StepWork(props: StepWorkProps) {
  const { step, video, heard, error, action, second } = props;
  const lines = workLines(props);
  const share = heardShare(video);
  const row = (key: WorkStep, title: string) => {
    const st = rowState(step, key);
    return (
      <div className={cx("nbwrow", st)} key={key}>
        <span className="o">{st === "done" ? <Icon name="check" /> : st === "run" ? <span className="spin" /> : null}</span>
        <div>
          <b>{title}</b>
          <span>{lines[key]}</span>
        </div>
      </div>
    );
  };
  return (
    <div className="card nbw">
      <StepHead n={WORK_ORDER.indexOf(step) + 1} of={7} title={WORK_HEAD[step]} />
      <div className="nbwork">
        <div>
          {step === "playable" ? (
            <div className="nbwv nbwv-pieces">
              <div className="nbcells" aria-label={lines.playable}>
                {Array.from({ length: Math.max(video.chunkCount, 1) }, (_, i) => (
                  <i key={i} className={i < video.chunksDone ? "on" : ""} />
                ))}
              </div>
            </div>
          ) : (
            <div className="nbwv nbwv-listen">
              <div className="nbframev">
                {video.posterUrl ? <img src={video.posterUrl} alt="" /> : null}
                <div className="nbwave" aria-hidden="true">
                  {wave.map((h, i) => (
                    <i key={i} className={i / WAVE_BARS <= share ? "on" : ""} style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
              <div className="nbheard" aria-live="polite">
                {heard.length ? (
                  heard.slice(-3).map((l, i, a) => (
                    <div className={cx("nbline", i === a.length - 1 && "in")} key={`${l.ts}-${i}`}>
                      <span>{l.ts}</span>
                      {l.said}
                    </div>
                  ))
                ) : (
                  <div className="nbline muted">Listening from the first second…</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="nbwrows">
          {row("upload", "Upload")}
          {row("playable", "Make it playable")}
          {row("listen", "Listen to the talk")}
        </div>
      </div>
      {error ? (
        <div className="note warn mt16" role="alert">
          <Icon name="info" />
          <div>
            <span>{error}</span>
            {action || second ? (
              <div className="row g8 mt8">
                {action ? (
                  <button type="button" className="btn s" onClick={action.onClick}>
                    {action.label}
                  </button>
                ) : null}
                {second ? (
                  <button type="button" className="btn s q" onClick={second.onClick}>
                    {second.label}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="small muted mt20">You can leave this page. It keeps going.</div>
      )}
    </div>
  );
}

// ── Step 4: the names ────────────────────────────────────────────────────────

/**
 * The names check: the names the talk repeats, each with what it should say. Nothing to check (or already checked) goes
 * straight on. Shared by the computer's card and the phone's.
 */
export function useNamesCheck(webinarId: string, onChecked: () => void) {
  const [names, setNames] = useState<HeardNameView[] | null>(null);
  const [said, setSaid] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const checked = useRef(onChecked);
  checked.current = onChecked;

  useEffect(() => {
    let live = true;
    void api.board
      .names(webinarId)
      .then((res) => {
        if (!live) return;
        // Nothing to check (or already checked): the read goes on without a stop.
        if (res.checked || res.names.length === 0) {
          checked.current();
          return;
        }
        setNames(res.names);
      })
      .catch(() => {
        // Never trap the host behind a card that cannot load: the read goes on.
        if (live) checked.current();
      });
    return () => {
      live = false;
    };
  }, [webinarId]);

  const confirm = async () => {
    if (!names) return;
    setSending(true);
    setError("");
    const fixes = names
      .map((n) => ({ from: n.name, to: (said[n.name] ?? n.name).trim() }))
      .filter((f) => f.to && f.to !== f.from);
    try {
      await api.board.fixNames(webinarId, fixes);
      checked.current();
    } catch {
      setError("That did not save. Try again.");
      setSending(false);
    }
  };
  return { names, said, setSaid, sending, error, confirm };
}

export function StepNames({ webinarId, minutes, onChecked }: { webinarId: string; minutes: number; onChecked: () => void }) {
  const { names, said, setSaid, sending, error, confirm } = useNamesCheck(webinarId, onChecked);

  if (names === null) return <div className="card nbw nbload" aria-busy="true" />;
  return (
    <div className="card nbw">
      <StepHead n={4} of={7} title="Did we hear these names right?" />
      <div className="okcard nbok">
        <div className="big">
          <Icon name="check" />
        </div>
        <div>
          <div className="b8">Your talk is ready</div>
          {minutes ? <div className="small ink2">{minutes} minutes</div> : null}
        </div>
      </div>
      <div className="nbnames">
        {names.map((n, i) => {
          const first = n.mentions[0];
          return (
            <div className="nbname" key={n.name}>
              <div className="nbname-h">
                <b>{n.name}</b>
                <span>
                  {n.count} {n.count === 1 ? "mention" : "mentions"}
                  {first ? ` · heard at ${first.ts}` : ""}
                </span>
              </div>
              <div className="field">
                <label htmlFor={`nbn${i}`}>It should say</label>
                <input dir="auto"
                  id={`nbn${i}`}
                  className="inp"
                  value={said[n.name] ?? n.name}
                  onChange={(e) => setSaid((s) => ({ ...s, [n.name]: e.target.value }))}
                />
              </div>
            </div>
          );
        })}
      </div>
      {error ? (
        <p className="note warn mt16" role="alert">
          <Icon name="info" />
          <span>{error}</span>
        </p>
      ) : null}
      <div className="nbw-f">
        <button type="button" className="btn p lg" disabled={sending} onClick={() => void confirm()}>
          {sending ? "Saving the names…" : "Looks right, build the board"} <Icon name="right" />
        </button>
      </div>
    </div>
  );
}

// ── Step 5: the board being built ────────────────────────────────────────────

/** What each of the read's ten steps is called while it runs. */
export const SCENE_NAMES: Record<SceneStep, string> = {
  read: "Reading the whole talk",
  title: "Picking the title",
  desc: "Writing the description",
  chapters: "Finding the chapters",
  quotes: "Lifting the lines worth quoting",
  faq: "Writing the questions people ask",
  moments: "Placing the moments in the room",
  offer: "Spotting the offer",
  followups: "Planning the follow-ups",
  [READ_PAGE_STEP]: "Laying out the page",
};
export const SCENES = Object.keys(SCENE_NAMES) as SceneStep[];

type TileKey = "page" | "chapters" | "moments" | "follows";
/** When a tile is written (every step landed) and when it starts (one landed). */
const TILE_STEPS: Record<TileKey, { done: SceneStep[]; from: SceneStep }> = {
  page: { done: ["title", "desc"], from: "read" },
  chapters: { done: ["chapters"], from: "desc" },
  moments: { done: ["moments", "offer"], from: "chapters" },
  follows: { done: ["followups"], from: "offer" },
};

function tileState(key: TileKey, landed: ReadonlySet<SceneStep>, built: boolean): RowState {
  if (built) return "done";
  const t = TILE_STEPS[key];
  if (t.done.every((s) => landed.has(s))) return "done";
  return landed.has(t.from) ? "run" : "wait";
}

const MOMENT_WORD: Partial<Record<BoardCard["kind"], string>> = {
  poll: "Poll",
  question: "Question",
  reaction: "Reaction",
  handout: "Handout",
};

function momentLine(c: BoardCard): string {
  if (c.kind === "poll" || c.kind === "question") return c.q;
  if (c.kind === "reaction") return c.prompt;
  if (c.kind === "handout") return c.name;
  return c.label;
}

export function StepBuild({
  readProgress,
  built,
  onNext,
  error,
  action,
}: {
  readProgress: SceneStep[] | null;
  /** The board the read wrote, once it has landed. */
  built: BoardState | null;
  onNext: () => void;
  error?: string;
  action?: StepAction;
}) {
  const landed = new Set(readProgress ?? []);
  const done = built !== null;
  const running = SCENES.find((s) => !landed.has(s)) ?? READ_PAGE_STEP;
  const at = SCENES.indexOf(running);
  const title = built?.page.find((c) => c.kind === "title");
  const desc = built?.page.find((c) => c.kind === "desc");
  const chapters = built?.page.find((c) => c.kind === "chapters");
  const moments = (built?.room ?? []).filter((c) => MOMENT_WORD[c.kind]);
  const offer = built?.room.find((c) => c.kind === "offer");
  const follows = built?.mail ?? [];
  const tile = (key: TileKey, icon: Parameters<typeof Icon>[0]["name"], name: string, body: React.ReactNode) => {
    const st = tileState(key, landed, done);
    return (
      <div className={cx("nbtile", st)} key={key}>
        <div className="nbtile-h">
          <span className="nbk">
            <Icon name={icon} />
          </span>
          <b>{name}</b>
          <span className="sp" />
          {st === "done" ? (
            <span className="pill good">
              <Icon name="check" /> Ready
            </span>
          ) : st === "run" ? (
            <span className="pill brass">
              <span className="spin nbspin" /> Writing
            </span>
          ) : (
            <span className="pill">Waiting</span>
          )}
        </div>
        <div className="nbtile-b">
          {st === "done" && done ? (
            body
          ) : (
            <>
              <i className="sk" />
              <i className="sk" />
              <i className="sk s" />
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="card nbw">
      <StepHead n={5} of={7} title={done ? "Your board is built" : "Building the board"} sub={done ? undefined : "About a minute"} />
      <div className="nbtiles">
        {tile(
          "page",
          "door",
          "The page",
          <>
            <div className="nbtile-title">{title && title.kind === "title" ? title.v : ""}</div>
            <div className="small ink2 clamp2">{desc && desc.kind === "desc" ? desc.v : ""}</div>
          </>,
        )}
        {tile(
          "chapters",
          "list",
          "Chapters",
          <div className="nbtile-list">
            {chapters && chapters.kind === "chapters"
              ? chapters.rows.slice(0, 4).map((r) => (
                  <div key={`${r.t}-${r.title}`}>
                    <span>{mmss(r.t)}</span>
                    {r.title}
                  </div>
                ))
              : null}
            {chapters && chapters.kind === "chapters" && chapters.rows.length > 4 ? (
              <div className="muted">and {chapters.rows.length - 4} more</div>
            ) : null}
          </div>,
        )}
        {tile(
          "moments",
          "sparkle",
          "Moments in the room",
          <div className="nbtile-list">
            {moments.map((m) => (
              <div key={m.id}>
                <span>{mmss(m.t ?? 0)}</span>
                {MOMENT_WORD[m.kind]}: {momentLine(m)}
              </div>
            ))}
            {offer && offer.kind === "offer" ? (
              <div>
                <span>{mmss(offer.t ?? 0)}</span>The offer: {offer.btn}
              </div>
            ) : null}
          </div>,
        )}
        {tile(
          "follows",
          "mail",
          "Follow-ups",
          <div className="nbtile-list">
            {follows.map((f) => (
              <div key={f.id}>
                <span>
                  <Icon name="mail" />
                </span>
                {f.label}
              </div>
            ))}
          </div>,
        )}
      </div>
      {done ? null : (
        <div className="nbcap" aria-live="polite">
          <span className="spin" />
          <b>{SCENE_NAMES[running]}</b>
          <span className="muted">
            {at + 1} of {SCENES.length}
          </span>
        </div>
      )}
      {error ? (
        <div className="note warn mt16" role="alert">
          <Icon name="info" />
          <div>
            <span>{error}</span>
            {action ? (
              <div className="row g8 mt8">
                <button type="button" className="btn s" onClick={action.onClick}>
                  {action.label}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {done ? (
        <div className="nbw-f">
          <button type="button" className="btn p lg" onClick={onNext}>
            Check the chapters <Icon name="right" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
